import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Monogram } from "./Monogram";
import { NAV_LINKS } from "../data/site";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "border-b border-white/10 bg-black/80 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
            <Monogram size="sm" className="transition-colors duration-500 group-hover:border-[#C04B58]" />
            <span className="leading-none">
              <span className="font-display block text-xl font-semibold tracking-wide text-white">SHUTEN</span>
              <span className="font-mono2 block text-[9px] uppercase tracking-[0.35em] text-zinc-500">Tattoo Studio · Budapest</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" data-testid="nav-desktop">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace("ő", "o").replace("á", "a").replace("é", "e")}`}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-zinc-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    <span
                      className={`absolute -bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#9A3542] transition-opacity duration-300 ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </>
                )}
              </NavLink>
            ))}
            <Link to="/kapcsolat" data-testid="nav-appointment-btn" className="btn-primary !px-6 !py-2.5">
              Időpont
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-white lg:hidden"
            aria-label="Menü"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0A0A0A]/97 px-8 backdrop-blur-xl lg:hidden"
          >
            {NAV_LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.4 }}
              >
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display block border-b border-[#1c1c1e] py-4 text-4xl font-medium ${
                      isActive ? "text-[#C04B58]" : "text-zinc-200"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
              <Link to="/kapcsolat" data-testid="nav-mobile-appointment-btn" className="btn-primary mt-8">
                Időpontkérés
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
